import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { appActivityKeys, appDraftKeys } from '~/lib/query-keys';

/**
 * FRONT-2943: an app's Activity (`GET /api/admin/apps/:slug/activity`), for
 * whoever the app's `seesActivity` admits (FRONT-3368), a code change's
 * changes on demand, and the owner's undo, publish undo and restore.
 */

export interface ActivityItem {
  id: number;
  type: string;
  at: string;
  actor: { id: string | null; name: string | null };
  person: { id: string; name: string | null } | null;
  label: string;
  hasChanges: boolean;
  /** The owner's: `change` undoes this code change, `publish` this live publish. */
  undo: 'change' | 'publish' | null;
  publishId: number | null;
  /** The owner's: undoing this publish takes the live app offline (it is the one that took it live). */
  undoTakesOffline: boolean;
  /** The owner's: the version to restore. */
  restore: string | null;
}

export interface ActivityPage {
  items: ActivityItem[];
  nextCursor: string | null;
}

export interface ChangedFile {
  path: string;
  status: 'added' | 'changed' | 'removed';
  hunks: Array<{ before: string; after: string }>;
  /** No lines: binary; past the Activity's total; or, for a draft, past what the draft's bound left for it. */
  note?: 'binary' | 'too large' | 'not shown';
  /** A draft's file shown only in part. */
  truncated?: true;
}

const base = (slug: string) => `/api/admin/apps/${encodeURIComponent(slug)}`;

export function useAppActivity(slug: string, enabled = true) {
  return useInfiniteQuery<ActivityPage, Error, { pages: ActivityPage[] }, readonly unknown[], string | null>({
    queryKey: appActivityKeys.list(slug),
    enabled: enabled && !!slug,
    initialPageParam: null,
    getNextPageParam: (last) => last.nextCursor,
    queryFn: ({ pageParam }) => api.get<ActivityPage>(`${base(slug)}/activity${pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : ''}`),
  });
}

export function useActivityChanges(slug: string, eventId: number, enabled: boolean) {
  return useQuery({
    queryKey: appActivityKeys.changes(slug, eventId),
    queryFn: () => api.get<{ files: ChangedFile[] }>(`${base(slug)}/activity/${eventId}/changes`),
    enabled: enabled && !!slug,
    staleTime: Infinity,
  });
}

/** Undo, publish undo and restore answer with the server's own sentence on refusal; the list, the drafts and the app are read again either way. */
function useActivityAction<TArg, TAnswer>(slug: string, request: (arg: TArg) => Promise<TAnswer>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: appActivityKeys.list(slug) }),
        qc.invalidateQueries({ queryKey: appDraftKeys.list(slug) }),
        qc.invalidateQueries({ queryKey: ['admin-app', slug] }),
      ]),
  });
}

export function useUndoChange(slug: string) {
  return useActivityAction(slug, (eventId: number) => api.post<{ undone: true; rebuilt: boolean }>(`${base(slug)}/activity/${eventId}/undo`, {}));
}

/** Undoes the publish `publishId`; the server refuses unless that one is still live. */
export function undoPublishRequest(slug: string, publishId: number) {
  return api.post<{ undone: true; change: { eventId: number } | null; wentOffline: boolean }>(`${base(slug)}/publishes/${publishId}/undo`, {});
}

export function useUndoPublish(slug: string) {
  return useActivityAction(slug, (publishId: number) => undoPublishRequest(slug, publishId));
}

export function useRestoreVersion(slug: string) {
  return useActivityAction(slug, (commit: string) => api.post<{ restored: true; moved: boolean }>(`${base(slug)}/restore`, { commit }));
}
